import { memo, Fragment } from 'react';
import { Checkbox } from '@mui/material';
import Flex from '../../../generic/Flex';
import type { AllPermissionsItem } from './hooks/useAllPermissions';
import useAllPermissions, { permissionIsChecked } from './hooks/useAllPermissions';
import Loader from '../../../generic/Loader';
import { TextInput } from '../../../generic/Forms';
import type { PermissionGroup } from '../../../../../typings/api';
import type { RoleScope } from '../../../../../typings/enums';
import { plural } from '../../../../../shared/helpers';
import type { RolePermissions } from '../../../../../shared/model-typings';

type AllPermissionsTableHeaderProps = {
  isAllPermissionsChecked: boolean;
  searchQuery: string;
  onCheckAllPermissions: () => void;
  onSearchQueryChange: (value: string) => void;
};

/**
 * Header w/Search bar for searcing for permissions
 *
 * @param {AllPermissionsTableHeaderProps} params - The props for this component.
 * @param {boolean} params.isAllPermissionsChecked - If all available permissions are checked.
 * @param {string} params.searchQuery - The permission search query.
 * @param {() => void} params.onCheckAllPermissions - Checkbox change handler for checking/unchecking all.
 * @param {(value: string) => void} params.onSearchQueryChange - Change handler for the text input.
 */
const AllPermissionsTableHeader = ({
  isAllPermissionsChecked,
  searchQuery,
  onCheckAllPermissions,
  onSearchQueryChange,
}: AllPermissionsTableHeaderProps) => (
  <header className="roles__all-permissions-table__header">
    <Flex justify="flex-start">
      <Checkbox checked={isAllPermissionsChecked} onChange={onCheckAllPermissions} />
      <TextInput
        label="Find a permission"
        placeholder="Find a permission"
        value={searchQuery}
        onChange={onSearchQueryChange}
      />
    </Flex>
  </header>
);

type AllPermissionsTableFooterProps = {
  allCount: number;
  hiddenCount: number;
};

/**
 * Displays the number of currently selected permissions,
 * including the number which are hidden because of the search query.
 *
 * @param {AllPermissionsTableFooterProps} params - The props for this component.
 * @param {number} params.allCount - The number of permissions which are currently selected.
 * @param {number} params.hiddenCount - The number of permissions which are selected but not visible due to the search query.
 */
const AllPermissionsTableFooter = ({ allCount, hiddenCount }: AllPermissionsTableFooterProps) => (
  <footer className="roles__all-permissions-table__footer">
    <span>{`${allCount} ${plural(allCount, 'permission')} selected`}</span>
    {hiddenCount > 0 && (
      <span className="roles__all-permissions-table__footer__hidden-count">
        {`(${hiddenCount} ${plural(hiddenCount, 'permission')} hidden)`}
      </span>
    )}
  </footer>
);

type AllPermissionsTableProps = {
  isReadOnly: boolean;
  scope: RoleScope;
  rolePermissions: RolePermissions;
  updatePermissions: (permissions: AllPermissionsItem[]) => void;
  setEditingPermissions: (permissions: AllPermissionsItem[]) => void;
};

/**
 *  Table which displays all permissions in a grouped checkbox list.
 */
const AllPermissionsTable = ({
  scope,
  isReadOnly,
  rolePermissions,
  updatePermissions,
  setEditingPermissions,
}: AllPermissionsTableProps) => {
  const { state, dispatch, groups } = useAllPermissions({
    scope,
    isReadOnly,
    rolePermissions,
    updatePermissions,
    setEditingPermissions,
  });

  return state.isFetching ? (
    <Loader />
  ) : (
    <div className="roles__all-permissions-table">
      <AllPermissionsTableHeader
        isAllPermissionsChecked={state.isAllPermissionsChecked}
        searchQuery={state.searchQuery}
        onCheckAllPermissions={dispatch.onToggleAll}
        onSearchQueryChange={dispatch.onSearchQueryChange}
      />
      <div className="roles__all-permissions-table__body">
        {groups.length === 0 ? (
          <div className="roles__all-permissions-table__body__empty">No permissions match your search.</div>
        ) : (
          groups.map(([group, permissions]) => {
            const isGroupAllChecked = permissions.every(permissionIsChecked);
            return (
              <Fragment key={group}>
                <div className="roles__all-permissions-table__body__item roles__all-permissions-table__body__item--group-heading">
                  <Checkbox
                    id={`permission-group--${group}`}
                    checked={isGroupAllChecked}
                    onChange={() => dispatch.onToggleGroup(group as PermissionGroup)}
                  />
                  <label className="margin-left-sm" htmlFor={`permission-group--${group}`}>
                    {group}
                  </label>
                </div>
                {permissions.map((permission) => (
                  <div key={permission.id} className="roles__all-permissions-table__body__item">
                    <Checkbox
                      id={permission.id}
                      checked={permission.isChecked}
                      onChange={() => dispatch.onTogglePermission(permission.id)}
                    />
                    <label className="margin-left-sm" htmlFor={permission.id}>
                      {permission.description}
                    </label>
                  </div>
                ))}
              </Fragment>
            );
          })
        )}
      </div>
      {state.permissionsSelected.all > 0 && (
        <AllPermissionsTableFooter
          allCount={state.permissionsSelected.all}
          hiddenCount={state.permissionsSelected.hidden}
        />
      )}
    </div>
  );
};

export default memo(AllPermissionsTable);
